import React from 'react';
import { MessageCircle, Send, AlertCircle, CheckCircle, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function WhatsAppTab({ 
  whatsappSettings, 
  setWhatsappSettings, 
  whatsappStatus,
  handleWhatsappSettingsUpdate,
  testPhone,
  setTestPhone,
  testWhatsappMessage,
  testingSending
}) {
  const [copiedWebhook, setCopiedWebhook] = React.useState(false);
  
  const webhookUrl = `${window.location.origin}/api/whatsapp/webhook`;
  
  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopiedWebhook(true);
    toast.success('Webhook URL copied!');
    setTimeout(() => setCopiedWebhook(false), 2000);
  };

  return (
    <div data-testid="whatsapp-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c] mb-6 flex items-center">
        <MessageCircle className="h-7 w-7 mr-3" />
        WhatsApp Business API
      </h2>

      {/* Status Card */}
      <div className={`mb-6 p-4 rounded-lg border ${
        whatsappStatus.configured && whatsappStatus.enabled 
          ? 'bg-green-50 border-green-200' 
          : 'bg-yellow-50 border-yellow-200'
      }`}>
        <div className="flex items-center">
          {whatsappStatus.configured && whatsappStatus.enabled ? (
            <>
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-green-800">WhatsApp notifications are configured and enabled</span>
            </>
          ) : (
            <>
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
              <span className="text-yellow-800">
                {!whatsappStatus.configured 
                  ? 'WhatsApp API not configured. Enter your credentials below.' 
                  : 'WhatsApp API is configured but disabled'}
              </span>
            </>
          )}
        </div>
      </div>

      <form onSubmit={handleWhatsappSettingsUpdate} className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
        <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg">
          <div>
            <h3 className="font-medium text-gray-900">Enable WhatsApp Notifications</h3>
            <p className="text-sm text-gray-500">Send order and payment confirmations via WhatsApp</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={whatsappSettings.whatsapp_api_enabled}
              onChange={(e) => setWhatsappSettings({ ...whatsappSettings, whatsapp_api_enabled: e.target.checked })}
              className="sr-only peer"
              data-testid="whatsapp-enabled-toggle"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1a3a5c]"></div>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Access Token (Permanent Token)
            </label>
            <input
              type="password"
              value={whatsappSettings.whatsapp_access_token}
              onChange={(e) => setWhatsappSettings({ ...whatsappSettings, whatsapp_access_token: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="EAAxxxxxx..."
              data-testid="whatsapp-token-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Get from Meta Business Suite → Settings → Business Settings → System Users → Generate Token
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number ID
            </label>
            <input
              type="text"
              value={whatsappSettings.whatsapp_phone_number_id}
              onChange={(e) => setWhatsappSettings({ ...whatsappSettings, whatsapp_phone_number_id: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="1234567890123456"
              data-testid="whatsapp-phone-id-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              From WhatsApp Manager → Phone Numbers
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Account ID
            </label>
            <input
              type="text"
              value={whatsappSettings.whatsapp_business_account_id}
              onChange={(e) => setWhatsappSettings({ ...whatsappSettings, whatsapp_business_account_id: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="1234567890123456"
              data-testid="whatsapp-business-id-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              App Secret (Optional)
            </label>
            <input
              type="password"
              value={whatsappSettings.whatsapp_app_secret}
              onChange={(e) => setWhatsappSettings({ ...whatsappSettings, whatsapp_app_secret: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="For webhook signature verification"
              data-testid="whatsapp-secret-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Webhook Verify Token
            </label>
            <input
              type="text"
              value={whatsappSettings.whatsapp_webhook_verify_token}
              onChange={(e) => setWhatsappSettings({ ...whatsappSettings, whatsapp_webhook_verify_token: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="Any secure string"
              data-testid="whatsapp-verify-token-input"
            />
          </div>

          <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Webhook URL (for Meta configuration)
            </label>
            <div className="flex">
              <input
                type="text"
                value={webhookUrl}
                readOnly
                className="flex-1 px-4 py-2 bg-gray-100 border border-gray-300 rounded-l-sm text-gray-600"
              />
              <button
                type="button"
                onClick={copyWebhookUrl}
                className="px-4 py-2 bg-[#1a3a5c] text-white rounded-r-sm hover:bg-[#1f4268]"
              >
                {copiedWebhook ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div className="md:col-span-2 border-t border-gray-200 pt-4 mt-2">
            <h4 className="font-medium text-gray-900 mb-4">Message Template Names</h4>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Order Confirmation Template
            </label>
            <input
              type="text"
              value={whatsappSettings.whatsapp_order_template_name}
              onChange={(e) => setWhatsappSettings({ ...whatsappSettings, whatsapp_order_template_name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="order_confirmation"
              data-testid="order-template-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Confirmation Template
            </label>
            <input
              type="text"
              value={whatsappSettings.whatsapp_payment_template_name}
              onChange={(e) => setWhatsappSettings({ ...whatsappSettings, whatsapp_payment_template_name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="payment_confirmation"
              data-testid="payment-template-input"
            />
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            type="submit"
            className="bg-[#1a3a5c] text-white px-6 py-2 rounded-sm hover:bg-[#1f4268] flex items-center space-x-2"
            data-testid="save-whatsapp-settings"
          >
            <span>Save WhatsApp Settings</span>
          </button>
        </div>
      </form>

      {/* Test Message Section */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-4">Send Test Message</h3>
        <p className="text-sm text-gray-600 mb-4">
          Enter a phone number (with country code) to send a test message and verify your configuration.
        </p>
        <div className="flex space-x-3">
          <input
            type="tel"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
            placeholder="+919876543210"
            data-testid="test-phone-input"
          />
          <button
            type="button"
            onClick={testWhatsappMessage}
            disabled={testingSending || !testPhone}
            className="px-6 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            data-testid="send-test-whatsapp"
          >
            <Send className="h-4 w-4" />
            <span>{testingSending ? 'Sending...' : 'Send Test'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
